import { useState } from 'react';
import type { FinancePnlDataQualityStatus, RevenueDailyDay } from '@nadoutong/contracts';
import { useFinancePnl } from '../../api/finance.ts';
import { useOpsUsageOverview } from '../../api/opsUsage.ts';
import { usePricing } from '../../api/pricing.ts';
import { useRevenueDaily, useRevenueSummary } from '../../api/revenue.ts';
import {
  TrendBucketTable,
  TrendChart,
  TrendViewToggle,
  rollupDailyBuckets,
} from '../../components/charts/TrendChart.tsx';
import type {
  TrendChartSeries,
  TrendRollup,
  TrendTableColumn,
  TrendView,
} from '../../components/charts/TrendChart.tsx';
import {
  formatCnyCents,
  formatUsdMicrosOrCentsAsCnyDisplay,
  usdMicrosFromCentsFallback,
  usdMicrosToCnyCents,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, KpiCard, Panel, Skeleton } from '../../ui/index.ts';
import type { MetricsTabContext } from './hubState.ts';

/**
 * 总览 tab (epic #1643): the cross-source KPI wall + the daily revenue/
 * profit trend. Three independent queries feed three KPI sections —
 * request/token facts (gateway usage), revenue/cost/L2 (revenue
 * ledger), true operating profit (finance pnl). Each section degrades
 * alone: a failed source renders an unavailable note, never a blank
 * page.
 */

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
const TREND_DAY_OPTIONS = [30, 60, 90] as const;

const QUALITY_KEYS: Record<FinancePnlDataQualityStatus, I18nKey> = {
  balanced: 'metrics.overview.quality.balanced',
  partial: 'metrics.overview.quality.partial',
  unreliable: 'metrics.overview.quality.unreliable',
};

export function OverviewTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const { window, brand, customFrom, customTo } = context;
  const custom = window === 'custom';
  const usage = useOpsUsageOverview({
    window,
    from: custom ? customFrom : undefined,
    to: custom ? customTo : undefined,
    brand: brand === '' ? undefined : brand,
  });
  const summary = useRevenueSummary({ window, brand, from: customFrom, to: customTo });
  // today/7d/30d/custom are all valid FinancePnlWindow values, so the
  // shared window maps 1:1 here.
  const pnl = useFinancePnl({ window, brand, from: customFrom, to: customTo });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const rateValid = typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
  const [trendDays, setTrendDays] = useState<number>(30);
  const [trendView, setTrendView] = useState<TrendView>('chart');
  const [trendRollup, setTrendRollup] = useState<TrendRollup>('day');
  const daily = useRevenueDaily(trendDays, brand);

  return (
    <div className="flex flex-col gap-6" data-testid="metrics-overview-tab">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {usage.isPending ? (
          <SectionSkeleton count={2} testId="metrics-overview-usage-loading" />
        ) : usage.isError ? (
          <SectionUnavailable label={t('metrics.overview.usage_error')} testId="metrics-overview-usage-error" />
        ) : usage.data !== undefined ? (
          <>
            <KpiCard
              testId="metrics-overview-requests"
              label={t('metrics.overview.requests')}
              value={formatCount(usage.data.totals.requestCount)}
              delta={{
                text: t('metrics.overview.requests.sub', {
                  success: formatCount(usage.data.totals.successCount),
                  error: formatCount(usage.data.totals.errorCount),
                }),
                tone: 'neutral',
              }}
            />
            <KpiCard
              testId="metrics-overview-tokens"
              label={t('metrics.overview.tokens')}
              value={formatCount(usage.data.totals.inputTokens + usage.data.totals.outputTokens)}
              delta={{
                text: t('metrics.overview.tokens.sub', {
                  input: formatCount(usage.data.totals.inputTokens),
                  output: formatCount(usage.data.totals.outputTokens),
                  cacheRead: formatCount(usage.data.totals.cacheReadTokens),
                }),
                tone: 'neutral',
              }}
            />
          </>
        ) : null}

        {summary.isPending ? (
          <SectionSkeleton count={3} testId="metrics-overview-revenue-loading" />
        ) : summary.isError ? (
          <SectionUnavailable label={t('metrics.overview.revenue_error')} testId="metrics-overview-revenue-error" />
        ) : summary.data !== undefined ? (
          <>
            <KpiCard
              testId="metrics-overview-revenue"
              label={t('metrics.overview.revenue')}
              tone="ok"
              value={formatUsdMicrosOrCentsAsCnyDisplay(
                summary.data.usage.usd_charged_micros,
                summary.data.usage.usd_charged_cents,
                cnyPerUsd, false)}
              delta={{
                text: t('metrics.overview.revenue.sub', {
                  amount: formatUsdMicrosOrCentsAsCnyDisplay(
                    summary.data.usage.gift_card_funded_usd_micros,
                    summary.data.usage.gift_card_funded_usd_cents,
                    cnyPerUsd, false),
                }),
                tone: 'neutral',
              }}
            />
            <KpiCard
              testId="metrics-overview-upstream-cost"
              label={t('metrics.overview.upstream_cost')}
              tone="bad"
              value={formatCnyCents(summary.data.usage.cny_upstream_cost_cents, false)}
              delta={{ text: t('metrics.overview.upstream_cost.sub'), tone: 'neutral' }}
            />
            <KpiCard
              testId="metrics-overview-l2-profit"
              label={t('metrics.overview.l2_profit')}
              tone={summary.data.usage.l2_profit_cny_cents < 0 ? 'bad' : 'default'}
              value={formatCnyCents(summary.data.usage.l2_profit_cny_cents, true)}
              delta={{
                text: t('metrics.overview.l2_profit.sub', {
                  pct: formatPct(summary.data.usage.l2_margin_pct),
                }),
                tone: 'neutral',
              }}
            />
          </>
        ) : null}

        {pnl.isPending ? (
          <SectionSkeleton count={1} testId="metrics-overview-pnl-loading" />
        ) : pnl.isError ? (
          <SectionUnavailable label={t('metrics.overview.pnl_error')} testId="metrics-overview-pnl-error" />
        ) : pnl.data !== undefined ? (
          <KpiCard
            testId="metrics-overview-operating-profit"
            label={
              <span className="flex w-full items-center justify-between gap-2">
                {t('metrics.overview.operating_profit')}
                <Chip
                  tone={
                    pnl.data.data_quality.status === 'balanced'
                      ? 'ok'
                      : pnl.data.data_quality.status === 'partial'
                        ? 'warn'
                        : 'bad'
                  }
                  testId="metrics-overview-quality-chip"
                >
                  {t(QUALITY_KEYS[pnl.data.data_quality.status])}
                </Chip>
              </span>
            }
            tone={pnl.data.operating.realized_operating_profit_cny_cents < 0 ? 'bad' : 'default'}
            value={formatCnyCents(pnl.data.operating.realized_operating_profit_cny_cents, true)}
            delta={{ text: t('metrics.overview.operating_profit.sub'), tone: 'neutral' }}
          />
        ) : null}
      </div>

      <Panel
        caption={t('metrics.overview.trend.title')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div
              role="tablist"
              aria-label={t('metrics.trend.days_aria')}
              className="inline-flex gap-1"
            >
              {TREND_DAY_OPTIONS.map((option) => {
                const active = option === trendDays;
                return (
                  <Button
                    key={option}
                    role="tab"
                    aria-selected={active}
                    variant={active ? 'secondary' : 'ghost'}
                    size="sm"
                    className={active ? 'bg-active text-paper' : undefined}
                    data-testid={`metrics-overview-trend-days-${option}`}
                    onClick={() => setTrendDays(option)}
                  >
                    {t('metrics.trend.days', { days: option })}
                  </Button>
                );
              })}
            </div>
            <TrendViewToggle
              view={trendView}
              onViewChange={setTrendView}
              rollup={trendRollup}
              onRollupChange={setTrendRollup}
              testIdPrefix="metrics-overview-trend"
            />
          </div>
        }
        padded
        testId="metrics-overview-trend"
      >
        {daily.isPending ? (
          <Skeleton className="h-56" testId="metrics-overview-trend-loading" />
        ) : daily.isError ? (
          <p className="m-0 text-body text-bad-bright" data-testid="metrics-overview-trend-error">
            {t('metrics.overview.trend.error')}
          </p>
        ) : daily.data !== undefined ? (
          <div className="flex flex-col gap-3">
            {/* Rate missing → the revenue bars are withheld (a ¥0 bar
                would be a lie); say so explicitly next to the chart. */}
            {!rateValid ? (
              <Chip tone="warn" testId="metrics-overview-trend-rate-unavailable">
                {t('metrics.overview.trend.rate_unavailable')}
              </Chip>
            ) : null}
            {trendView === 'chart' ? (
              <TrendChart
                labels={daily.data.days.map((day) => day.date)}
                series={buildDailySeries(daily.data.days, rateValid ? cnyPerUsd : null, {
                  revenue: t('metrics.overview.trend.revenue'),
                  profit: t('metrics.overview.trend.profit'),
                })}
                valueFormatter={(value) => formatCnyCents(value, value < 0)}
                ariaLabel={t('metrics.overview.trend.title')}
                emptyLabel={t('metrics.overview.trend.empty')}
                testId="metrics-overview-trend-chart"
              />
            ) : (
              <OverviewTrendTable
                days={daily.data.days}
                cnyPerUsd={rateValid ? cnyPerUsd : null}
                rollup={trendRollup}
              />
            )}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

/**
 * Revenue bars (usage USD charged → display CNY cents) + L2 profit
 * line (already CNY cents). The revenue series is only drawn when the
 * display rate is available — an unknown rate must not render ¥0
 * revenue; the page surfaces an explicit rate-unavailable chip in that
 * case.
 */
function buildDailySeries(
  days: readonly RevenueDailyDay[],
  cnyPerUsd: number | null,
  labels: { readonly revenue: string; readonly profit: string },
): readonly TrendChartSeries[] {
  const profit: TrendChartSeries = {
    key: 'l2-profit',
    label: labels.profit,
    kind: 'line',
    tone: 'ok',
    values: days.map((day) => day.l2_profit_cny_cents),
  };
  const revenueValues: number[] = [];
  for (const day of days) {
    const cnyCents = usdMicrosToCnyCents(
      usdMicrosFromCentsFallback(day.usage_usd_charged_micros, day.usage_usd_charged_cents),
      cnyPerUsd,
    );
    if (cnyCents === null) {
      return [profit];
    }
    revenueValues.push(cnyCents);
  }
  const revenue: TrendChartSeries = {
    key: 'revenue',
    label: labels.revenue,
    kind: 'bar',
    tone: 'signal',
    values: revenueValues,
  };
  return [revenue, profit];
}

/**
 * The 表格 twin of the daily trend chart (#1675): one row per bucket
 * with 营收 / L2 利润 / derived 毛利率, rolled up client-side to ISO
 * weeks or UTC+8 calendar months on demand. When the display FX rate
 * is unavailable the revenue and margin cells render `—` (mirrors the
 * chart withholding the revenue bars — a ¥0 cell would be a lie).
 */
function OverviewTrendTable({
  days,
  cnyPerUsd,
  rollup,
}: {
  readonly days: readonly RevenueDailyDay[];
  readonly cnyPerUsd: number | null;
  readonly rollup: TrendRollup;
}): JSX.Element {
  const t = useT();
  const labels = days.map((day) => day.date);
  const profitValues = days.map((day) => day.l2_profit_cny_cents);
  const revenueValues: number[] = [];
  let revenueKnown = true;
  for (const day of days) {
    const cnyCents = usdMicrosToCnyCents(
      usdMicrosFromCentsFallback(day.usage_usd_charged_micros, day.usage_usd_charged_cents),
      cnyPerUsd,
    );
    if (cnyCents === null) {
      revenueKnown = false;
      break;
    }
    revenueValues.push(cnyCents);
  }
  const rolled = rollupDailyBuckets(
    labels,
    revenueKnown ? [revenueValues, profitValues] : [profitValues],
    rollup,
  );
  const rolledRevenue = revenueKnown ? (rolled.series[0] ?? []) : null;
  const rolledProfit = (revenueKnown ? rolled.series[1] : rolled.series[0]) ?? [];
  const columns: readonly TrendTableColumn[] = [
    {
      key: 'revenue',
      header: t('metrics.overview.trend.revenue'),
      cells: rolled.labels.map((_, index) =>
        rolledRevenue === null ? '—' : formatCnyCents(rolledRevenue[index] ?? 0, false),
      ),
    },
    {
      key: 'profit',
      header: t('metrics.overview.trend.profit'),
      cells: rolled.labels.map((_, index) => formatCnyCents(rolledProfit[index] ?? 0, true)),
    },
    {
      key: 'margin',
      header: t('metrics.models.col.margin'),
      cells: rolled.labels.map((_, index) => {
        if (rolledRevenue === null) return '—';
        const revenue = rolledRevenue[index] ?? 0;
        if (revenue <= 0) return '—';
        return formatPct((rolledProfit[index] ?? 0) / revenue);
      }),
    },
  ];
  return (
    <TrendBucketTable
      bucketHeader={t('metrics.chart.col.bucket')}
      labels={rolled.labels}
      columns={columns}
      emptyLabel={t('metrics.overview.trend.empty')}
      note={rollup === 'day' ? undefined : t('metrics.chart.rollup.note')}
      testId="metrics-overview-trend-table"
    />
  );
}

function SectionSkeleton({ count, testId }: { readonly count: number; readonly testId: string }): JSX.Element {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="h-[124px]" testId={index === 0 ? testId : undefined} />
      ))}
    </>
  );
}

function SectionUnavailable({ label, testId }: { readonly label: string; readonly testId: string }): JSX.Element {
  return (
    <div
      className="flex min-h-[124px] items-center rounded-lg border border-bad-line bg-bad-dim/30 p-4 text-body text-bad-bright"
      data-testid={testId}
    >
      {label}
    </div>
  );
}

function formatCount(value: number): string {
  return TOKEN_FORMATTER.format(value);
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}
